import type {
  BlockerConfigurationOutput,
  BlockerCreateInit,
  BlockerDetailsOutput,
  BlockerGeneralOutput,
} from "../../../../../_shared/_logic/create-blocker-contract"
import {
  MAX_BLOCKER_SERIES_OCCURRENCES,
  buildOccurrenceDays,
  resolveSeriesStartTime,
} from "../../../../../_shared/_logic/events-create-blocker-series-planning"

export type BlockerPayloadBuildResult =
  | {
      ok: true
      payloads: ScheduleEventCreatePayload[]
      skippedPast: number
      skippedConflicts: number
    }
  | { ok: false; reason: string }

const cleanText = (value?: string | null) => {
  const text = String(value ?? "").trim()

  return text || null
}

const buildPayload = ({
  init,
  general,
  details,
  startAt,
  endAt,
  allDay,
}: {
  init: BlockerCreateInit
  general: BlockerGeneralOutput
  details: BlockerDetailsOutput
  startAt: string
  endAt: string
  allDay: boolean
}): ScheduleEventCreatePayload => ({
  organization_id: init.organizationId,
  employee_id: general.employeeId,
  event_type: general.eventType,
  status: "CONFIRMED",
  source: "MANUAL",
  flex_type: "HARD",
  planning_flag: "OK",
  start_at: startAt,
  end_at: endAt,
  timeframe_start_at: startAt,
  timeframe_end_at: endAt,
  all_day: allDay,
  title: cleanText(details.title),
  description: cleanText(details.description),
  customer_note: null,
  location_text: cleanText(details.locationText),
  property_id: null,
  work_order_id: null,
})

const validDate = (value: Date | null): Date | null => {
  if (!value || Number.isNaN(value.getTime())) return null

  return new Date(value)
}

const allDayBounds = (day: Date) => ({
  startAt: new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())).toISOString(),
  endAt: new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate() + 1)).toISOString(),
})

const resolveDurationMinutes = (value: number | null) => {
  if (!Number.isFinite(value) || value <= 0) return null

  const rounded = Math.round(value)

  return rounded > 0 ? rounded : null
}

const individualBounds = ({
  day,
  rawTime,
  durationMinutes,
}: {
  day: Date
  rawTime: string
  durationMinutes: number | null
}) => {
  const startTime = resolveSeriesStartTime({ rawTime })

  if (!startTime) return null

  const duration = resolveDurationMinutes(durationMinutes)

  if (!duration) return null

  const start = new Date(day)
  start.setHours(startTime.hours, startTime.minutes, 0, 0)
  const end = new Date(start.getTime() + duration * 60000)

  return { startAt: start.toISOString(), endAt: end.toISOString() }
}

export const buildBlockerCreatePayloads = ({
  init,
  general,
  configuration,
  details,
  now,
}: {
  init: BlockerCreateInit
  general: BlockerGeneralOutput
  configuration: BlockerConfigurationOutput
  details: BlockerDetailsOutput
  now: Date
}): BlockerPayloadBuildResult => {
  if (!cleanText(details.title)) return { ok: false, reason: "Bitte einen Titel angeben." }

  void now

  if (configuration.planningKind === "single") {
    const day = validDate(configuration.singleDate)

    if (!day) return { ok: false, reason: "Bitte ein gültiges Datum wählen." }

    const bounds =
      configuration.durationMode === "all_day"
        ? allDayBounds(day)
        : individualBounds({
            day,
            rawTime: configuration.startTime,
            durationMinutes: configuration.durationMinutes,
          })

    if (!bounds) return { ok: false, reason: "Bitte gültige Startzeit und Dauer wählen." }

    return {
      ok: true,
      payloads: [
        buildPayload({
          init,
          general,
          details,
          ...bounds,
          allDay: configuration.durationMode === "all_day",
        }),
      ],
      skippedPast: 0,
      skippedConflicts: 0,
    }
  }

  const occurrences = buildOccurrenceDays({
    rangeStart: configuration.seriesRangeStart,
    rangeEnd: configuration.seriesRangeEnd,
    pattern: configuration.seriesPattern,
    weekdays: configuration.seriesWeekdays,
    maxOccurrences: MAX_BLOCKER_SERIES_OCCURRENCES,
  })

  if (!occurrences.ok) return { ok: false, reason: occurrences.reason }

  const skippedPast = 0
  const skippedConflicts = 0
  const payloads: ScheduleEventCreatePayload[] = []
  for (const day of occurrences.days) {
    const bounds =
      configuration.durationMode === "all_day"
        ? allDayBounds(day)
        : individualBounds({
            day,
            rawTime: configuration.startTime,
            durationMinutes: configuration.durationMinutes,
          })

    if (!bounds) return { ok: false, reason: "Bitte gültige Startzeit und Dauer wählen." }

    payloads.push(
      buildPayload({
        init,
        general,
        details,
        ...bounds,
        allDay: configuration.durationMode === "all_day",
      }),
    )
  }

  if (!payloads.length) {
    return { ok: false, reason: "Keine passenden Serientermine gefunden." }
  }

  return { ok: true, payloads, skippedPast, skippedConflicts }
}
